import type { MailMessageSummary } from "@/lib/types/mail";
import { unifiedMessageKey } from "@/lib/unified/unifiedInboxMerge";

/** Délai avant marquage « lu » à l’ouverture d’un non lu (laisse le temps de supprimer sans réordonnancement). */
export const INBOX_MARK_READ_ON_OPEN_DELAY_MS = 2000;

/** Clé stable pour une ligne (uid seul ou uid+compte en réception unifiée). */
export function messageSelectionKey(msg: Pick<MailMessageSummary, "uid" | "accountId">): string {
  return msg.accountId != null ? unifiedMessageKey(msg.accountId, msg.uid) : String(msg.uid);
}

/** Message suivant dans la liste après suppression (en dessous, sinon au-dessus). */
export function pickNextMessageAfterRemove(
  messages: MailMessageSummary[],
  removedKeys: ReadonlySet<string>,
  anchor: Pick<MailMessageSummary, "uid" | "accountId">
): MailMessageSummary | null {
  const anchorKey = messageSelectionKey(anchor);
  let anchorIndex = messages.findIndex((m) => messageSelectionKey(m) === anchorKey);
  if (anchorIndex < 0) {
    anchorIndex = messages.findIndex((m) => m.uid === anchor.uid);
  }
  if (anchorIndex < 0) return null;

  for (let i = anchorIndex + 1; i < messages.length; i++) {
    const msg = messages[i]!;
    if (!removedKeys.has(messageSelectionKey(msg))) return msg;
  }
  for (let i = anchorIndex - 1; i >= 0; i--) {
    const msg = messages[i]!;
    if (!removedKeys.has(messageSelectionKey(msg))) return msg;
  }
  return null;
}

/** Ctrl/Cmd ou Shift enfoncé lors d’un clic. */
export function isMultiSelectClick(event?: React.MouseEvent | React.KeyboardEvent): boolean {
  return Boolean(event?.ctrlKey || event?.metaKey);
}

export function isRangeSelectClick(event?: React.MouseEvent | React.KeyboardEvent): boolean {
  return Boolean(event?.shiftKey);
}

export function selectUidRange(
  orderedUids: number[],
  fromUid: number,
  toUid: number
): number[] {
  const fromIndex = orderedUids.indexOf(fromUid);
  const toIndex = orderedUids.indexOf(toUid);
  if (fromIndex < 0 || toIndex < 0) return [toUid];
  const start = Math.min(fromIndex, toIndex);
  const end = Math.max(fromIndex, toIndex);
  return orderedUids.slice(start, end + 1);
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}
